import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';

export interface WorkflowStage {
  step: number;
  name: string;
  assignedRole: string;
  status: 'COMPLETED' | 'IN_PROGRESS' | 'PENDING' | 'REJECTED';
  actorName?: string;
  timestamp?: string;
  comment?: string;
}

@Component({
  selector: 'app-dsr-detail',
  standalone: true,
  imports: [CommonModule, RouterLink, FormsModule],
  template: `
    <div class="space-y-6">
      <!-- Top Navigation & Actions Bar -->
      <div class="flex items-center justify-between">
        <a routerLink="/dsr" class="px-3 py-1.5 rounded-xl text-xs font-semibold bg-white text-slate-700 hover:text-slate-900 border border-slate-200 flex items-center gap-2 transition shadow-2xs">
          <svg class="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Data Sharing Requests
        </a>

        <div class="flex items-center gap-2">
          <button (click)="openDecisionModal('RETURN')" class="px-3.5 py-2 rounded-xl text-xs font-bold bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 flex items-center gap-1.5 transition">
            Request Revision
          </button>
          <button (click)="openDecisionModal('REJECT')" class="px-3.5 py-2 rounded-xl text-xs font-bold bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 flex items-center gap-1.5 transition">
            Reject Request
          </button>
          <button (click)="openDecisionModal('APPROVE')" class="px-4 py-2 rounded-xl text-xs font-bold bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-500 hover:to-teal-500 shadow-md shadow-emerald-600/20 flex items-center gap-1.5 transition">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            Approve Current Stage
          </button>
        </div>
      </div>

      <!-- Main Request Banner -->
      <div class="bg-white rounded-2xl border border-slate-200 p-6 space-y-4 shadow-sm">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div class="flex items-center gap-3">
            <span class="font-mono text-xs font-bold text-emerald-800 px-3 py-1 rounded-lg bg-emerald-50 border border-emerald-200">
              {{ requestData().uid }}
            </span>
            <span class="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300">
              Stage {{ currentStep() }} of {{ stages().length }}: {{ getCurrentStageName() }}
            </span>
          </div>

          <span class="text-xs text-slate-600 font-semibold flex items-center gap-1.5 bg-slate-100 px-3 py-1 rounded-lg border border-slate-200">
            <svg class="w-3.5 h-3.5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Requested on {{ requestData().createdDate }}
          </span>
        </div>

        <div>
          <h1 class="text-2xl font-black text-slate-900">{{ requestData().title }}</h1>
          <p class="text-xs text-slate-600 mt-1 max-w-3xl leading-relaxed">{{ requestData().purpose }}</p>
        </div>

        <div class="grid grid-cols-2 sm:grid-cols-5 gap-3 pt-3 border-t border-slate-100">
          <div>
            <div class="text-[10px] uppercase font-bold text-slate-400">Request Date & Time</div>
            <div class="text-xs font-bold text-slate-900 mt-0.5">{{ requestData().createdDate }}</div>
          </div>
          <div>
            <div class="text-[10px] uppercase font-bold text-slate-400">Consumer Institution</div>
            <div class="text-xs font-bold text-slate-900 mt-0.5">{{ requestData().consumerInstitution }}</div>
          </div>
          <div>
            <div class="text-[10px] uppercase font-bold text-slate-400">Provider Institution</div>
            <div class="text-xs font-bold text-emerald-700 mt-0.5">{{ requestData().providerInstitution }}</div>
          </div>
          <div>
            <div class="text-[10px] uppercase font-bold text-slate-400">Target Service Endpoint</div>
            <div class="text-xs font-bold text-slate-900 mt-0.5">{{ requestData().targetService }}</div>
          </div>
          <div>
            <div class="text-[10px] uppercase font-bold text-slate-400">Legal Basis</div>
            <div class="text-xs font-bold text-purple-700 mt-0.5">{{ requestData().legalBasis }}</div>
          </div>
        </div>
      </div>

      <!-- FlowProgressPanel: Multi-Stage Progress Tracker -->
      <div class="bg-white rounded-2xl border border-slate-200 p-6 space-y-4 shadow-sm">
        <h3 class="text-sm font-bold text-slate-900 flex items-center gap-2">
          <svg class="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          Multi-Stage Approval Workflow Progress
        </h3>

        <div class="grid grid-cols-1 md:grid-cols-4 gap-3 relative">
          <div *ngFor="let stage of stages(); let i = index" class="p-4 rounded-xl border relative transition shadow-2xs"
            [ngClass]="{
              'bg-emerald-50 border-emerald-300': stage.status === 'COMPLETED',
              'bg-emerald-100/70 border-emerald-400 ring-2 ring-emerald-300': stage.status === 'IN_PROGRESS',
              'bg-slate-50 border-slate-200': stage.status === 'PENDING',
              'bg-rose-50 border-rose-300': stage.status === 'REJECTED'
            }">
            
            <div class="flex items-center justify-between mb-2">
              <span class="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                [ngClass]="{
                  'bg-emerald-600 text-white': stage.status === 'COMPLETED',
                  'bg-teal-600 text-white animate-pulse': stage.status === 'IN_PROGRESS',
                  'bg-slate-200 text-slate-600': stage.status === 'PENDING'
                }">
                {{ stage.step }}
              </span>
              <span class="text-[10px] font-bold uppercase tracking-wider text-slate-500">{{ stage.assignedRole }}</span>
            </div>

            <div class="font-bold text-xs text-slate-900">{{ stage.name }}</div>

            <div *ngIf="stage.status === 'COMPLETED'" class="mt-2 text-[10px] text-emerald-700 font-bold">
              ✓ Approved by {{ stage.actorName }}
              <div class="text-slate-500 text-[9px] font-normal">{{ stage.timestamp }}</div>
            </div>

            <div *ngIf="stage.status === 'IN_PROGRESS'" class="mt-2 text-[10px] text-teal-800 font-bold">
              ⏳ Action Required
            </div>

            <div *ngIf="stage.status === 'PENDING'" class="mt-2 text-[10px] text-slate-400">
              Pending previous stage
            </div>
          </div>
        </div>
      </div>

      <!-- Verified SLA & Policy Acceptance Certificate -->
      <div class="bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 rounded-2xl border border-emerald-200 p-5 space-y-3 shadow-2xs">
        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div class="flex items-center gap-3">
            <div class="p-2 rounded-xl bg-emerald-100 text-emerald-800 border border-emerald-300">
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <h4 class="text-sm font-extrabold text-slate-900">Verified SLA & Data Sharing Terms Acceptance Certificate</h4>
              <p class="text-xs text-slate-600">Electronically signed and verified by {{ requestData().consumerInstitution }} prior to request submission.</p>
            </div>
          </div>

          <span class="px-3 py-1 rounded-full text-xs font-extrabold bg-emerald-100 text-emerald-800 border border-emerald-300 shrink-0">
            ✓ SLA Active & Bound
          </span>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 text-xs border-t border-emerald-200/60">
          <div class="p-2.5 rounded-xl bg-white border border-slate-200">
            <span class="text-[10px] text-slate-500 font-semibold block">Agreed Uptime Target:</span>
            <strong class="text-emerald-700 text-sm">99.90% SLA</strong>
          </div>
          <div class="p-2.5 rounded-xl bg-white border border-slate-200">
            <span class="text-[10px] text-slate-500 font-semibold block">Max Latency Limit:</span>
            <strong class="text-sky-700 text-sm">&le; 250ms Target</strong>
          </div>
          <div class="p-2.5 rounded-xl bg-white border border-slate-200">
            <span class="text-[10px] text-slate-500 font-semibold block">Data Protection Act:</span>
            <strong class="text-purple-700 text-sm">Compliant (Sec. 14 & 18)</strong>
          </div>
        </div>
      </div>

      <!-- Two-Column Layout: Requested Data Scope & Attachments -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Requested Fields Panel -->
        <div class="bg-white rounded-2xl border border-slate-200 p-5 space-y-3 shadow-sm">
          <h3 class="text-sm font-bold text-slate-900">Requested Data Attributes & Fields</h3>
          <div class="flex flex-wrap gap-2 pt-2">
            <span *ngFor="let field of requestData().dataFields" class="px-3 py-1.5 rounded-xl bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs font-mono font-bold flex items-center gap-1.5">
              <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
              {{ field }}
            </span>
          </div>
        </div>

        <!-- Attached Documents Panel -->
        <div class="bg-white rounded-2xl border border-slate-200 p-5 space-y-3 shadow-sm">
          <h3 class="text-sm font-bold text-slate-900">Compliance & Legal Attachments</h3>
          <div class="space-y-2">
            <div *ngFor="let doc of requestData().attachments" class="p-3 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-between text-xs">
              <div class="flex items-center gap-2">
                <svg class="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span class="font-bold text-slate-800">{{ doc.name }}</span>
              </div>
              <span class="text-[10px] text-emerald-800 font-bold bg-emerald-100 px-2 py-0.5 rounded border border-emerald-300">Verified</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Decision Modal -->
      <div *ngIf="showDecisionModal()" class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
        <div class="bg-white border border-slate-200 rounded-2xl max-w-md w-full p-6 space-y-4 shadow-2xl">
          <div class="flex items-center justify-between border-b border-slate-200 pb-3">
            <h3 class="text-base font-bold text-slate-900">
              Submit Stage Decision: {{ decisionAction() }}
            </h3>
            <button (click)="showDecisionModal.set(false)" class="text-slate-400 hover:text-slate-900">✕</button>
          </div>

          <div class="space-y-3 text-xs">
            <div>
              <label class="block text-slate-700 font-bold mb-1">Reviewer Comment / Official Notes</label>
              <textarea [(ngModel)]="reviewerNotes" rows="4" placeholder="Provide justification or required amendments for this request..." class="w-full bg-white border border-slate-300 text-slate-900 rounded-xl px-3 py-2 focus:border-emerald-500 focus:outline-none"></textarea>
            </div>
          </div>

          <div class="flex items-center justify-end gap-3 pt-3 border-t border-slate-200">
            <button (click)="showDecisionModal.set(false)" class="px-4 py-2 rounded-xl text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-300">Cancel</button>
            <button (click)="confirmDecision()" class="px-4 py-2 rounded-xl text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-500 shadow-sm">Confirm & Record Decision</button>
          </div>
        </div>
      </div>
    </div>
  `
})
export class DsrDetailComponent implements OnInit {
  requestUid = 'DSR-ZAN-2026-884';
  currentStep = signal(2);
  showDecisionModal = signal(false);
  decisionAction = signal<'APPROVE' | 'REJECT' | 'RETURN'>('APPROVE');
  reviewerNotes = '';

  requestData = signal({
    uid: 'DSR-ZAN-2026-884',
    title: 'Realtime Citizen Identification & Verification for Revenue Registration',
    consumerInstitution: 'Zanzibar Revenue Authority (ZRA)',
    providerInstitution: 'ZanID Registration Agency',
    targetService: 'Citizen Verification Service Endpoint (v1)',
    createdDate: '2026-07-15 09:30 AM',
    legalBasis: 'Zanzibar Data Protection & e-Gov Act (Sec. 18)',
    purpose: 'Automated identity verification of individual tax filers during tax identification number (TIN) issuance to eliminate duplicate registrations.',
    dataFields: ['National ID Number (NIN)', 'First Name', 'Surname', 'Date of Birth', 'Biometric Verification Hash'],
    attachments: [
      { name: 'Bilateral Data Sharing Agreement (DSA_ZRA_ZANID.pdf)' },
      { name: 'Data Protection Impact Assessment (DPIA_Approved_2026.pdf)' }
    ]
  });

  stages = signal<WorkflowStage[]>([
    { step: 1, name: 'Consumer Internal Approval', assignedRole: 'Institutional Admin', status: 'COMPLETED', actorName: 'Salum Juma (ZRA)', timestamp: '2026-07-16 10:14' },
    { step: 2, name: 'DPO Privacy Impact Audit', assignedRole: 'Data Protection Officer', status: 'IN_PROGRESS' },
    { step: 3, name: 'Provider Technical Node Review', assignedRole: 'Custodian Admin', status: 'PENDING' },
    { step: 4, name: 'e-Gov Regulator Final Authorization', assignedRole: 'SuperAdmin', status: 'PENDING' }
  ]);

  constructor(private route: ActivatedRoute) {}

  ngOnInit() {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.requestUid = id;
    }
  }

  getCurrentStageName(): string {
    const curr = this.stages().find(s => s.step === this.currentStep());
    return curr ? curr.name : 'Completed';
  }

  openDecisionModal(action: 'APPROVE' | 'REJECT' | 'RETURN') {
    this.decisionAction.set(action);
    this.reviewerNotes = '';
    this.showDecisionModal.set(true);
  }

  confirmDecision() {
    const action = this.decisionAction();
    if (action === 'APPROVE') {
      this.stages.update(list => list.map(s => {
        if (s.step === this.currentStep()) {
          return { ...s, status: 'COMPLETED', actorName: 'Active User (DPO)', timestamp: new Date().toISOString().substring(0, 16).replace('T', ' ') };
        }
        if (s.step === this.currentStep() + 1) {
          return { ...s, status: 'IN_PROGRESS' };
        }
        return s;
      }));
      this.currentStep.update(c => Math.min(c + 1, this.stages().length));
    }
    this.showDecisionModal.set(false);
  }
}
